/**
 * kbAudience — chi vede quale contenuto.
 *
 * Modulo PURO e CONDIVISO: la stessa decisione deve valere per il widget in home,
 * per la pagina delle novità, per la ricerca lato Cloud Function e per l'anteprima
 * «questo annuncio raggiungerà N persone» in console. Se divergessero, l'editore
 * pubblicherebbe per un pubblico e ne raggiungerebbe un altro.
 *
 * ⚠️ CONFINE DI SICUREZZA — questo modulo NON è un confine di sicurezza. Il ruolo
 * e il piano sono filtri di RILEVANZA: l'unico confine imposto dal database è lo
 * `scope` (vedi le firestore rules). Il motivo è concreto e verificato: il claim
 * JWT `roles` si rinfresca solo con `setUserClaims`, quindi metterlo nelle rules
 * produrrebbe un `permission-denied` intermittente dipendente dall'età del token.
 * Documentazione davvero riservata a un'azienda si scrive come `scope: 'azienda'`
 * con il suo `tenant_id`: stesso meccanismo, zero flag nuovi.
 *
 * Doc canonico: docs/01-ARCHITECTURE/KB-CONTENT-MODEL.md
 */

import type { KbAudience, KbContenuto, KbVisibilitaTenant } from './kbTypes';

/** Chiave jolly: «tutti i ruoli». */
export const KB_CHIAVE_TUTTI = '*';

/** Prefisso delle chiavi di ruolo. */
export const KB_PREFISSO_RUOLO = 'ruolo:';

/**
 * Tetto di Firestore per `array-contains-any`. Le chiavi del lettore sono
 * `1 + numero di ruoli dell'utente`, quindi il tetto non si avvicina mai — ma il
 * cap resta esplicito perché superarlo farebbe fallire l'INTERA query, non solo
 * il filtro.
 */
export const KB_MAX_CHIAVI_QUERY = 30;

/**
 * Chiavi da scrivere sul documento (campo derivato `audience_keys`).
 *
 * `['*']` quando il contenuto è per tutti — **non** l'espansione dei 14 ruoli:
 * così il registro dei ruoli non finisce dentro ogni documento e l'arrivo di un
 * quindicesimo ruolo non richiede un backfill di tutti i contenuti.
 */
export function buildAudienceKeys(audience: KbAudience | undefined | null): string[] {
  if (!audience || audience.tutti_i_ruoli) return [KB_CHIAVE_TUTTI];
  const ruoli = (audience.ruoli ?? [])
    .map((r) => String(r).trim())
    .filter((r) => r.length > 0);
  const unici = Array.from(new Set(ruoli)).sort();
  // Nessun ruolo dichiarato con `tutti_i_ruoli: false` significa «nessuno»: si
  // scrive un array vuoto, e la validazione in pubblicazione lo blocca. Non si
  // ripiega su `['*']`, che trasformerebbe un errore dell'editore in un invio a
  // tutta la base clienti.
  return unici.map((r) => `${KB_PREFISSO_RUOLO}${r}`);
}

/**
 * Chiavi con cui il lettore interroga (`array-contains-any`).
 * Include sempre il jolly, così i contenuti «per tutti» arrivano anche a un
 * utente senza ruoli riconosciuti.
 */
export function chiaviLettore(ruoli: readonly string[] | undefined | null): string[] {
  const puliti = (ruoli ?? [])
    .map((r) => String(r).trim())
    .filter((r) => r.length > 0);
  const unici = Array.from(new Set(puliti));
  const chiavi = [KB_CHIAVE_TUTTI, ...unici.map((r) => `${KB_PREFISSO_RUOLO}${r}`)];
  return chiavi.slice(0, KB_MAX_CHIAVI_QUERY);
}

/** Contesto del lettore: chi è, per quale azienda, con che piano. */
export interface KbContestoLettore {
  tenant_id: string | null;
  ruoli: readonly string[];
  /**
   * «Il tenant ha questa capability attiva?» — un PREDICATO, non un elenco.
   *
   * Deliberato: la risposta ha una sola fonte canonica per lato
   * (`checkCapability` nel frontend, `tenantHasCapability` nelle Cloud
   * Functions). Ricostruire qui l'insieme delle capability attive da
   * `modules.{base,addon,override,expired}` sarebbe una seconda
   * implementazione della stessa regola, destinata a divergere.
   */
  haCapability: (capabilityId: string) => boolean;
  /** Il super utente vede tutto, comprese le bozze e i contenuti riservati. */
  is_super_admin?: boolean;
}

/**
 * Predicato costruito da un elenco già risolto. Comodo per i test, per gli
 * script una-tantum e per l'anteprima in console dove le capability dei tenant
 * si leggono in blocco.
 */
export function predicatoDaElenco(
  capability: readonly string[]
): (capabilityId: string) => boolean {
  const insieme = new Set(capability);
  return (id: string) => insieme.has(id);
}

/** Perché un contenuto non è stato mostrato. Serve alla diagnosi, non all'utente. */
export type KbMotivoEsclusione =
  | 'scope_altro_tenant'
  | 'ruolo_non_incluso'
  | 'tenant_non_incluso'
  | 'tenant_escluso'
  | 'capability_richiesta_assente'
  | 'stato_non_pubblicato';

export interface KbContenutoVisibile<T extends KbContenuto = KbContenuto> {
  contenuto: T;
  /**
   * Capability che il tenant NON ha e che rende la funzione descritta non
   * utilizzabile. Il contenuto è comunque leggibile: la UI aggiunge una nota
   * informativa. `null` quando non c'è nulla da segnalare.
   */
  capability_mancante: string | null;
}

export interface KbEsitoFiltro<T extends KbContenuto = KbContenuto> {
  visibili: KbContenutoVisibile<T>[];
  /** Elenco degli scartati con il motivo: se un annuncio non compare, si sa perché. */
  esclusi: Array<{ id: string; motivo: KbMotivoEsclusione }>;
}

function valutaCapability(
  contesto: KbContestoLettore,
  capability: string | null | undefined
): boolean {
  if (!capability) return true;
  if (contesto.is_super_admin) return true;
  return contesto.haCapability(capability);
}

function tenantAmmesso(
  visibilita: KbVisibilitaTenant | undefined | null,
  tenantId: string | null
): { ok: true } | { ok: false; motivo: KbMotivoEsclusione } {
  const modalita = visibilita?.modalita ?? 'tutti';
  const elenco = visibilita?.tenant_ids ?? [];
  if (modalita === 'tutti') return { ok: true };
  if (!tenantId) {
    // Senza tenant non si può valutare inclusione/esclusione: si esclude.
    // Fail-closed voluto: meglio non mostrare che mostrare al pubblico sbagliato.
    return { ok: false, motivo: modalita === 'inclusi' ? 'tenant_non_incluso' : 'tenant_escluso' };
  }
  if (modalita === 'inclusi') {
    return elenco.includes(tenantId) ? { ok: true } : { ok: false, motivo: 'tenant_non_incluso' };
  }
  return elenco.includes(tenantId) ? { ok: false, motivo: 'tenant_escluso' } : { ok: true };
}

/**
 * Filtra i contenuti che il lettore può vedere e dice perché ha scartato gli altri.
 *
 * Cosa NASCONDE: appartenenza ad altro tenant, ruolo non incluso, targeting per
 * tenant, `capability_richiesta` assente (uso eccezionale), stato non pubblicato.
 * Cosa NON nasconde: `capability_informativa` assente → il contenuto si legge e
 * torna in `capability_mancante` perché la UI ci scriva una nota.
 */
export function filtraVisibili<T extends KbContenuto>(
  contenuti: readonly T[],
  contesto: KbContestoLettore
): KbEsitoFiltro<T> {
  const visibili: KbContenutoVisibile<T>[] = [];
  const esclusi: Array<{ id: string; motivo: KbMotivoEsclusione }> = [];
  const chiavi = new Set(chiaviLettore(contesto.ruoli));

  for (const c of contenuti) {
    if (!contesto.is_super_admin && c.stato !== 'pubblicato') {
      esclusi.push({ id: c.id, motivo: 'stato_non_pubblicato' });
      continue;
    }

    if (c.scope === 'azienda' && c.tenant_id && c.tenant_id !== contesto.tenant_id) {
      if (!contesto.is_super_admin) {
        esclusi.push({ id: c.id, motivo: 'scope_altro_tenant' });
        continue;
      }
    }

    const keys = c.audience_keys ?? buildAudienceKeys(c.audience);
    const ruoloOk = contesto.is_super_admin || keys.some((k) => chiavi.has(k));
    if (!ruoloOk) {
      esclusi.push({ id: c.id, motivo: 'ruolo_non_incluso' });
      continue;
    }

    const tOk = contesto.is_super_admin
      ? ({ ok: true } as const)
      : tenantAmmesso(c.visibilita_tenant, contesto.tenant_id);
    if (!tOk.ok) {
      esclusi.push({ id: c.id, motivo: tOk.motivo });
      continue;
    }

    if (!valutaCapability(contesto, c.visibilita_tenant?.capability_richiesta)) {
      esclusi.push({ id: c.id, motivo: 'capability_richiesta_assente' });
      continue;
    }

    const informativa = c.visibilita_tenant?.capability_informativa ?? null;
    const capability_mancante = valutaCapability(contesto, informativa) ? null : informativa;

    visibili.push({ contenuto: c, capability_mancante });
  }

  return { visibili, esclusi };
}

/**
 * Quante persone raggiungerebbe un contenuto, dato l'elenco degli utenti.
 * Serve all'anteprima in console: senza un numero davanti agli occhi si finisce
 * per annunciare tutto a tutti per pigrizia.
 */
export function contaDestinatari(
  contenuto: KbContenuto,
  utenti: ReadonlyArray<{ uid: string; tenant_id: string | null; ruoli: readonly string[] }>,
  capabilityPerTenant: Readonly<Record<string, readonly string[]>> = {}
): { totale: number; per_tenant: Record<string, number> } {
  const per_tenant: Record<string, number> = {};
  let totale = 0;

  for (const u of utenti) {
    const esito = filtraVisibili([contenuto], {
      tenant_id: u.tenant_id,
      ruoli: u.ruoli,
      haCapability: predicatoDaElenco(
        (u.tenant_id ? capabilityPerTenant[u.tenant_id] : []) ?? []
      ),
    });
    if (esito.visibili.length > 0) {
      totale += 1;
      const key = u.tenant_id ?? 'senza_azienda';
      per_tenant[key] = (per_tenant[key] ?? 0) + 1;
    }
  }

  return { totale, per_tenant };
}
